import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, TextField, Chip, CircularProgress,
  InputAdornment, Divider, Avatar, Snackbar, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { CiSearch } from "react-icons/ci";
import { useParams, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;
const LARAVEL_URL = process.env.REACT_APP_LARAVEL_API;

interface PortalProduct {
  id: number;
  productName: string;
  productShortDescription?: string;
  productImg?: string;
  // Note: `productPrice` (cost) is intentionally NOT consumed here — order
  // pricing must never fall back to cost. Priority is custom → wholesale → instore.
  salePrice?: string;       // in-store price
  wholesaleprice?: string;  // wholesale price
  custom_price?: number | null;
}

interface ShopInfo {
  id: number;
  customerCompanyName?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerImg?: string;
}

interface RecentOrderItem { product_id: number; name: string; qty: number; price: number; subtotal: number; unit?: string; }
interface RecentOrder { id: number; status: string; total: number; notes?: string; portal_note?: string; items: RecentOrderItem[]; created_at: string; }

const STATUS_COLORS: Record<string, string> = {
  pending: "#ff9800",
  confirmed: "#1976d2",
  processing: "#7b1fa2",
  delivered: "#2e7d32",
  cancelled: "#c62828",
};

const Portal: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();

  const [shop, setShop] = useState<ShopInfo | null>(null);
  const [products, setProducts] = useState<PortalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);

  const loadOrders = async () => {
    if (!shopId) return;
    try {
      const r = await fetch(`${API_URL}/restaurant-portal/${shopId}/orders`, {
        headers: { Accept: "application/json" },
      });
      const text = await r.text();
      let d: any = null;
      try { d = JSON.parse(text); } catch { return; }
      if (d?.success) setRecentOrders(d.data || []);
    } catch {}
  };

  useEffect(() => { loadOrders(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [shopId]);

  useEffect(() => {
    if (!shopId) return;
    fetch(`${API_URL}/restaurant-portal/${shopId}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error("Not found");
        setShop(d.shop);
        setProducts(d.products || []);
      })
      .catch(() => setError("Restaurant not found or not available."))
      .finally(() => setLoading(false));
  }, [shopId]);

  // Order pricing priority:
  //   1. custom_price (per-shop override)
  //   2. wholesaleprice (wholesale)
  //   3. salePrice (in-store)
  // We deliberately never fall back to productPrice (cost) — that would expose
  // unprofitable pricing to restaurants. Returns 0 if no valid price exists.
  const getPrice = (p: PortalProduct): number => {
    if (p.custom_price != null && Number(p.custom_price) > 0) return Number(p.custom_price);
    if (p.wholesaleprice && Number(p.wholesaleprice) > 0) return Number(p.wholesaleprice);
    if (p.salePrice && Number(p.salePrice) > 0) return Number(p.salePrice);
    return 0;
  };

  const cartItems = products.filter((p) => (cart[p.id] || 0) > 0);
  const cartTotal = cartItems.reduce((s, p) => s + getPrice(p) * cart[p.id], 0);
  const cartCount = Object.values(cart).reduce((s, v) => s + v, 0);

  const adjust = (id: number, delta: number) => {
    setCart((prev) => {
      const next = (prev[id] || 0) + delta;
      return { ...prev, [id]: Math.max(0, next) };
    });
  };

  const handleSubmit = async () => {
    if (cartItems.length === 0) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const items = cartItems.map((p) => ({
        product_id: p.id,
        name: p.productName,
        qty: cart[p.id],
        price: getPrice(p),
        subtotal: getPrice(p) * cart[p.id],
      }));
      const res = await fetch(`${API_URL}/restaurant-portal/${shopId}/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items, total: cartTotal, notes }),
      });

      // Safe parse — server may return HTML on 404/500/auth-redirect.
      const text = await res.text();
      let data: any = null;
      try { data = JSON.parse(text); }
      catch {
        const isHtml = /^\s*<!DOCTYPE|^\s*<html/i.test(text);
        throw new Error(
          isHtml
            ? `Order endpoint unavailable (status ${res.status}). Please refresh and try again.`
            : `Unexpected server response (status ${res.status}).`
        );
      }
      if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to submit order");

      setOrderId(data.data?.id);
      // Clear cart + notes — order has been accepted
      setCart({});
      setNotes("");
      setCartOpen(false);
      setSubmitSuccess(`Order #${data.data?.id} placed successfully`);
      // Refresh recent orders so the new one appears immediately
      loadOrders();
      // Auto-dismiss the success toast
      setTimeout(() => setSubmitSuccess(""), 4000);
    } catch (e: any) {
      console.error(e);
      setSubmitError(e?.message || "Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  };

  const resetOrder = () => {
    setCart({});
    setNotes("");
    setSubmitted(false);
    setOrderId(null);
    setCartOpen(false);
  };

  const filtered = products.filter((p) =>
    p.productName.toLowerCase().includes(search.toLowerCase())
  );

  const shopName =
    shop?.customerCompanyName ||
    [shop?.customerFirstName, shop?.customerLastName].filter(Boolean).join(" ") ||
    "Restaurant Portal";

  if (loading) {
    return (
      <Box
        sx={{
          position: "fixed", inset: 0, bgcolor: "#f5f6fa", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          position: "fixed", inset: 0, bgcolor: "#f5f6fa", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2,
        }}
      >
        <StorefrontIcon sx={{ fontSize: 60, color: "#c5cae9" }} />
        <Typography fontSize={16} color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  /* ── MAIN PORTAL ────────────────────────────────────────────────────── */
  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "#f5f6fa", zIndex: 9999, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #3949ab 60%, #5c6bc0 100%)",
          px: { xs: 2, md: 4 }, py: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {shop?.customerImg ? (
            <Avatar
              src={`${LARAVEL_URL}/${shop.customerImg}`}
              sx={{ width: 40, height: 40, border: "2px solid rgba(255,255,255,0.4)" }}
            />
          ) : (
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "10px",
                bgcolor: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.35)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 18 }}>
                {shopName.charAt(0).toUpperCase()}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 16, lineHeight: 1.2 }}>
              {shopName}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Order Portal</Typography>
          </Box>
        </Box>

        {/* Cart toggle */}
        <Button
          variant="contained"
          onClick={() => setCartOpen((v) => !v)}
          startIcon={<ShoppingCartIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: cartCount > 0 ? "#ff9800" : "rgba(255,255,255,0.2)",
            fontWeight: 700, fontSize: 13, borderRadius: "10px", px: 2,
            "&:hover": { bgcolor: cartCount > 0 ? "#f57c00" : "rgba(255,255,255,0.3)" },
            boxShadow: "none",
          }}
        >
          {cartCount > 0 ? `Cart (${cartCount}) • $${cartTotal.toFixed(2)}` : "Cart"}
        </Button>
      </Box>

      {/* Body: product list + cart panel */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Product list */}
        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, md: 2.5 } }}>
          <TextField
            size="small" fullWidth
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ fontSize: 16, color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, bgcolor: "#fff", borderRadius: "10px", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          {/* ── Stats row ───────────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.25, mb: 2 }}>
            {[
              { label: "Available Products", value: products.length, color: "#3949ab", bg: "#e8eaf6" },
              { label: "Cart Items",         value: cartCount,        color: "#ff9800", bg: "#fff3e0" },
              { label: "Submitted Orders",   value: recentOrders.filter(o => o.status === "pending").length, color: "#f57f17", bg: "#fff8e1" },
              { label: "Confirmed/Delivered",value: recentOrders.filter(o => ["confirmed","processing","delivered"].includes(o.status)).length, color: "#2e7d32", bg: "#e8f5e9" },
            ].map((s) => (
              <Box key={s.label} sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e8eaf0", px: 1.75, py: 1.5 }}>
                <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>{s.label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <Box sx={{ width: 6, height: 24, borderRadius: 1, bgcolor: s.color }} />
                  <Typography fontSize={22} fontWeight={800} color="#0f172a">{s.value}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* ── Products header ──────────────────────────────────────── */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, mt: 1 }}>
            <Typography fontSize={14} fontWeight={700} color="#0f172a">Available Products</Typography>
            <Typography fontSize={11} color="text.secondary">{filtered.length} of {products.length}</Typography>
          </Box>

          {filtered.length === 0 && (
            <Box textAlign="center" py={5} sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e8eaf0" }}>
              <Typography color="text.secondary" fontSize={13}>No products found</Typography>
            </Box>
          )}

          {/* ── Product LIST view ─────────────────────────────────────── */}
          {filtered.length > 0 && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e8eaf0", overflow: "hidden" }}>
              {/* Header row */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "44px 1fr 60px 80px 130px", sm: "44px 1fr 70px 90px 160px" },
                alignItems: "center", gap: 1.25, px: 1.75, py: 1, bgcolor: "#fafafa",
                borderBottom: "1px solid #f0f0f5",
              }}>
                <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} sx={{ display: { xs: "none", sm: "block" } }}>Img</Typography>
                <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} sx={{ gridColumn: { xs: "1 / span 2", sm: "auto" } }}>Product</Typography>
                <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Unit</Typography>
                <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} textAlign="right">Price</Typography>
                <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5} textAlign="right">Quantity</Typography>
              </Box>

              {filtered.map((p, i) => {
                const qty = cart[p.id] || 0;
                const price = getPrice(p);
                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "44px 1fr 60px 80px 130px", sm: "44px 1fr 70px 90px 160px" },
                      alignItems: "center", gap: 1.25, px: 1.75, py: 1.25,
                      borderBottom: i < filtered.length - 1 ? "1px solid #f5f5f8" : "none",
                      bgcolor: qty > 0 ? "#f5f7ff" : "transparent",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: qty > 0 ? "#eef2ff" : "#fafbff" },
                    }}
                  >
                    {p.productImg ? (
                      <Avatar src={`${LARAVEL_URL}/${p.productImg}`} variant="rounded" sx={{ width: 36, height: 36, borderRadius: "8px" }} />
                    ) : (
                      <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: "#e8eaf6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <StorefrontIcon sx={{ fontSize: 18, color: "#7986cb" }} />
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontSize={13} fontWeight={600} noWrap>{p.productName}</Typography>
                      {p.productShortDescription && (
                        <Typography fontSize={11} color="text.secondary" noWrap>{p.productShortDescription}</Typography>
                      )}
                    </Box>
                    <Typography fontSize={11} color="text.secondary">{(p as any).productConversionUnit || "—"}</Typography>
                    <Typography fontSize={13} fontWeight={700} color={price > 0 ? "#2e7d32" : "text.disabled"} textAlign="right">
                      {price > 0 ? `$${price.toFixed(2)}` : "—"}
                    </Typography>
                    {qty === 0 ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => adjust(p.id, 1)}
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        sx={{ borderRadius: "8px", fontWeight: 600, fontSize: 11, py: 0.5, borderColor: "#c5cae9", color: "#3949ab", "&:hover": { borderColor: "#3949ab", bgcolor: "#e8eaf6" } }}
                      >
                        Add to Cart
                      </Button>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                        <Button onClick={() => adjust(p.id, -1)} sx={{ minWidth: 28, width: 28, height: 28, p: 0, borderRadius: "8px", bgcolor: "#e8eaf6", color: "#3949ab", "&:hover": { bgcolor: "#c5cae9" } }}>
                          <RemoveIcon sx={{ fontSize: 14 }} />
                        </Button>
                        <Box sx={{ minWidth: 38, textAlign: "center" }}>
                          <Typography fontWeight={700} fontSize={14}>{qty}</Typography>
                          <Typography fontSize={9} color="text.secondary">${(price * qty).toFixed(2)}</Typography>
                        </Box>
                        <Button onClick={() => adjust(p.id, 1)} sx={{ minWidth: 28, width: 28, height: 28, p: 0, borderRadius: "8px", bgcolor: "#3949ab", color: "#fff", "&:hover": { bgcolor: "#1a237e" } }}>
                          <AddIcon sx={{ fontSize: 14 }} />
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── Recent Orders section ─────────────────────────────────── */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3, mb: 1 }}>
            <Typography fontSize={14} fontWeight={700} color="#0f172a">Recent Orders</Typography>
            <Typography fontSize={11} color="text.secondary">{recentOrders.length} order{recentOrders.length === 1 ? "" : "s"}</Typography>
          </Box>

          {recentOrders.length === 0 ? (
            <Box textAlign="center" py={4} sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e8eaf0" }}>
              <Typography fontSize={13} color="text.secondary">No orders yet — submit your first order from the cart.</Typography>
            </Box>
          ) : (
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e8eaf0", overflow: "hidden" }}>
              {recentOrders.map((o, i) => {
                const isOpen = expandedOrderId === o.id;
                const itemsArr: RecentOrderItem[] = Array.isArray(o.items) ? o.items : (typeof o.items === "string" ? JSON.parse(o.items) : []);
                const stMeta: Record<string, { label: string; bg: string; color: string }> = {
                  pending:    { label: "Submitted",    bg: "#fff8e1", color: "#f57f17" },
                  confirmed:  { label: "Confirmed",    bg: "#e8f5e9", color: "#2e7d32" },
                  processing: { label: "Under Review", bg: "#e3f2fd", color: "#1565c0" },
                  delivered:  { label: "Delivered",    bg: "#e8f5e9", color: "#2e7d32" },
                  cancelled:  { label: "Cancelled",   bg: "#ffebee", color: "#c62828" },
                };
                const meta = stMeta[o.status] || { label: o.status, bg: "#f5f5f5", color: "#666" };
                return (
                  <Box key={o.id} sx={{ borderBottom: i < recentOrders.length - 1 ? "1px solid #f0f0f5" : "none" }}>
                    <Box
                      onClick={() => setExpandedOrderId(isOpen ? null : o.id)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 90px 70px", sm: "100px 1fr 110px 90px 80px" },
                        alignItems: "center", gap: 1.25, px: 1.75, py: 1.25, cursor: "pointer",
                        "&:hover": { bgcolor: "#fafbff" },
                      }}
                    >
                      <Typography fontSize={13} fontWeight={700} color="#0f172a">#{o.id}</Typography>
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <Typography fontSize={11} color="text.secondary">{new Date(o.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</Typography>
                        <Typography fontSize={10} color="text.disabled">{new Date(o.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</Typography>
                      </Box>
                      <Chip label={meta.label} size="small" sx={{ fontSize: 10, fontWeight: 700, bgcolor: meta.bg, color: meta.color, height: 22, borderRadius: "11px" }} />
                      <Typography fontSize={12} fontWeight={600} color="#475569" textAlign="right">{itemsArr.length} item{itemsArr.length === 1 ? "" : "s"}</Typography>
                      <Typography fontSize={13} fontWeight={800} color="#2e7d32" textAlign="right">${Number(o.total).toFixed(2)}</Typography>
                    </Box>
                    {isOpen && (
                      <Box sx={{ px: 2, pb: 1.5, bgcolor: "#fafbff" }}>
                        <Divider sx={{ mb: 1 }} />
                        {itemsArr.map((it, idx) => (
                          <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "1fr 60px 80px 90px", gap: 1, py: 0.5 }}>
                            <Typography fontSize={12} fontWeight={500}>{it.name}</Typography>
                            <Typography fontSize={11} color="text.secondary" textAlign="right">×{it.qty}</Typography>
                            <Typography fontSize={11} color="text.secondary" textAlign="right">${Number(it.price).toFixed(2)}</Typography>
                            <Typography fontSize={12} fontWeight={700} textAlign="right">${Number(it.subtotal).toFixed(2)}</Typography>
                          </Box>
                        ))}
                        {(o.notes || o.portal_note) && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: "#fff", borderRadius: "8px", border: "1px solid #f0f0f5" }}>
                            {o.portal_note && <Typography fontSize={11} color="text.secondary"><b>Your note:</b> {o.portal_note}</Typography>}
                            {o.notes && <Typography fontSize={11} color="text.secondary"><b>Supplier note:</b> {o.notes}</Typography>}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Cart sidebar (desktop: always visible if cartOpen or items in cart; mobile: slide-in) */}
        {(cartOpen || cartCount > 0) && (
          <Box
            sx={{
              width: { xs: "100%", sm: 300 },
              position: { xs: "absolute", sm: "relative" },
              right: 0, top: 0, bottom: 0,
              bgcolor: "#fff",
              borderLeft: { sm: "1px solid #e8eaf0" },
              display: "flex", flexDirection: "column",
              zIndex: 10,
              boxShadow: { xs: "-4px 0 20px rgba(0,0,0,0.12)", sm: "none" },
            }}
          >
            <Box sx={{ px: 2, py: 1.75, borderBottom: "1px solid #f0f0f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography fontWeight={700} fontSize={15}>Order Summary</Typography>
              <Button size="small" onClick={() => setCartOpen(false)} sx={{ display: { sm: "none" }, minWidth: 0, p: 0.5 }}>✕</Button>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              {cartItems.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <ShoppingCartIcon sx={{ fontSize: 36, color: "#c5cae9", mb: 1 }} />
                  <Typography fontSize={13} color="text.secondary">No items yet</Typography>
                </Box>
              ) : (
                cartItems.map((p) => (
                  <Box key={p.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography fontSize={12} fontWeight={600} noWrap>{p.productName}</Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {cart[p.id]} × ${getPrice(p).toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography fontSize={13} fontWeight={700}>${(getPrice(p) * cart[p.id]).toFixed(2)}</Typography>
                    <Button
                      size="small"
                      onClick={() => setCart((prev) => { const n = {...prev}; delete n[p.id]; return n; })}
                      sx={{ minWidth: 0, ml: 0.5, p: 0.4, color: "#e53935", fontSize: 12 }}
                    >✕</Button>
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ p: 2, borderTop: "1px solid #f0f0f5" }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                <Typography fontWeight={700} fontSize={14}>Total</Typography>
                <Typography fontWeight={800} fontSize={14} color="#2e7d32">${cartTotal.toFixed(2)}</Typography>
              </Box>
              <TextField
                size="small" fullWidth multiline rows={2}
                label="Order notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
              <Button
                fullWidth variant="contained"
                disabled={cartItems.length === 0 || submitting}
                onClick={handleSubmit}
                sx={{
                  borderRadius: "10px", py: 1.25, fontWeight: 700, fontSize: 14,
                  bgcolor: "#3949ab", "&:hover": { bgcolor: "#1a237e" },
                  "&:disabled": { bgcolor: "#c5cae9" },
                }}
              >
                {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : `Submit Order · $${cartTotal.toFixed(2)}`}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={() => setSubmitError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={() => setSubmitError("")}
          severity="error"
          variant="filled"
          sx={{ borderRadius: "12px", fontSize: 13, fontWeight: 500, boxShadow: "0 8px 24px rgba(220,38,38,0.25)" }}
        >
          {submitError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!submitSuccess}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 99999, mt: 2 }}
      >
        <Alert
          icon={<CheckCircleIcon sx={{ fontSize: 22 }} />}
          severity="success"
          variant="filled"
          sx={{ borderRadius: "12px", fontSize: 14, fontWeight: 600, py: 1.25, px: 2.5, boxShadow: "0 12px 30px rgba(46,125,50,0.3)", bgcolor: "#16a34a", color: "#fff" }}
        >
          {submitSuccess}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Portal;
